import { createIsomorphicFn } from '@tanstack/react-start';
import { Effect } from 'effect';
import { authClient } from '@/lib/auth-client';
import { clientRuntime } from '@/runtimes/client-runtime';
import { getServerSessionImpl } from './get-session.server';
import {
  orUnavailable,
  remoteSessionCheck,
  toSessionCheck,
} from './get-session-shared';
import type { SessionCheck } from './get-session-shared';

export type SessionInfo = {
  userId: string;
  email: string;
  role: string | null;
};

export type { SessionCheck } from './get-session-shared';

const clientSessionCheck = (): Effect.Effect<SessionCheck> =>
  orUnavailable(
    remoteSessionCheck(() => authClient.getSession()).pipe(
      Effect.map(toSessionCheck),
    ),
  ).pipe(Effect.annotateLogs({ module: 'GetSession' }));

/**
 * Isomorphic session check using the better-auth client SDK.
 * - SSR: verifies the signed better-auth cookie cache locally (no network),
 *   falling back to the auth API only when the cache is absent or expired
 * - Client: uses the default authClient (same-origin, browser sends cookies)
 *
 * No createServerFn needed — createIsomorphicFn handles the split at compile time.
 */
export const getSession: () => Promise<SessionCheck> = createIsomorphicFn()
  .server(() => getServerSessionImpl())
  .client(() => clientRuntime.runPromise(clientSessionCheck()));
